import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Button, Checkbox, Divider, FormControlLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

export const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showNotification } = useNotification();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(email, password);
      showNotification('Login successful!', 'success');
      // Redirect based on user role
      if (response.user.role === 'host') {
        navigate('/host/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    } catch (error: any) {
      // Extract the actual error message from the API response
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' } }}>
        Welcome To <span style={{ color: '#377dff' }}>BellHop Express</span>
      </Typography>
      <Box sx={{ mb: 3, color: '#b0b8c1', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sign in to continue</Box>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: '#4B5563' },
            '&:hover fieldset': { borderColor: '#2563EB' },
            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
          },
          '& .MuiInputLabel-root': { color: '#9CA3AF' },
          '& .MuiInputBase-input': { color: '#fff' },
        }}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                sx={{ color: '#9CA3AF' }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: '#4B5563' },
            '&:hover fieldset': { borderColor: '#2563EB' },
            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
          },
          '& .MuiInputLabel-root': { color: '#9CA3AF' },
          '& .MuiInputBase-input': { color: '#fff' },
        }}
      />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        justifyContent: 'space-between', 
        mt: 1,
        gap: { xs: 1, sm: 0 }
      }}>
        <FormControlLabel
          control={<Checkbox checked={remember} onChange={e => setRemember(e.target.checked)} sx={{ color: '#2563EB' }} />}
          label={<Typography sx={{ color: '#b0b8c1', fontSize: { xs: 12, sm: 14 } }}>Remember Me</Typography>}
        />
        <Button
          onClick={() => navigate('/forgot-password')}
          sx={{ 
            color: '#b0b8c1', 
            textTransform: 'none', 
            fontSize: { xs: 12, sm: 14 }, 
            p: { xs: 0, sm: 1 },
            '&:hover': { color: '#2563EB', background: 'none' } 
          }}
        >
          Forgot Password?
        </Button>
      </Box>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        sx={{
          mt: 3,
          mb: 2,
          py: 1.5,
          backgroundColor: '#2563EB',
          '&:hover': {
            backgroundColor: '#1D4ED8',
          },
        }}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>
      <Divider sx={{ my: 2, color: '#374151', fontWeight: 500 }}>OR</Divider>
      <Button
        fullWidth
        variant="contained"
        startIcon={<GoogleIcon />}
        sx={{
          mb: 1,
          background: '#232933',
          color: '#fff',
          border: '1px solid #374151',
          textTransform: 'none',
          '&:hover': { background: '#181e25', borderColor: '#2563EB', color: '#2563EB' },
        }}
      >
        Login with Google
      </Button>
      <Button
        fullWidth
        variant="contained"
        startIcon={<FacebookIcon />}
        sx={{
          background: '#232933',
          color: '#fff',
          border: '1px solid #374151',
          textTransform: 'none',
          '&:hover': { background: '#181e25', borderColor: '#2563EB', color: '#2563EB' },
        }}
      >
        Login with Facebook
      </Button>
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#b0b8c1', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          Don&apos;t have an account?{' '}
          <Button
            onClick={() => navigate('/register')}
            sx={{ 
              color: '#2563EB', 
              textTransform: 'none', 
              fontWeight: 600, 
              fontSize: { xs: 12, sm: 14 }, 
              p: 0, 
              minWidth: 0 
            }}
          >
            Sign Up
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}; 